<template>
    <Datatable
        name="Transactions"
        @search="setSearch"
        :pagination.sync="pagination"
        :loading="loading"
        :hideActions="hideActions"
    >
        <template v-slot:filters v-if="!params.payable_type && !params.payable_id">
            <TypesFilter v-model="selectedTypes" />
            <Dropdown
                class="me-1 mb-1"
                :color="selectedPayableType ? 'success' : 'secondary'"
                :title="
                    selectedPayableType
                        ? `Payable type: ${formatPayableType(selectedPayableType)}`
                        : 'Filter by payable type'
                "
                small
            >
                <li v-for="{ type, name } in payableTypes" :key="type">
                    <button
                        type="button"
                        class="dropdown-item text-xs"
                        :class="type === selectedPayableType ? 'bg-primary text-white' : 'bg-white'"
                        @click="togglePayableType(type)"
                    >
                        {{ name }}
                    </button>
                </li>
            </Dropdown>
        </template>
        <template v-slot:thead>
            <tr>
                <th>ID</th>
                <th>Date</th>
                <th>Payment Method</th>
                <th>Amount</th>
                <th>Payable</th>
                <th></th>
            </tr>
        </template>
        <tr
            valign="middle"
            @click="openTransactionPanel(transaction)"
            v-for="(transaction, index) in transactions"
            :key="'transaction-' + index"
        >
            <td>
                <code>{{ transaction.id }}</code>
            </td>
            <td>
                <div>{{ transaction.created_at | formatDate('DD/MM/YYYY HH:mm:ss') }}</div>
            </td>
            <td>
                <SelectedPaymentMethod
                    :paymentMethod="{ payment_method: transaction.payment_method }"
                    :clickable="false"
                    smallIcons
                />
            </td>
            <td>{{ transaction.amount | currency }}</td>
            <td>
                <Payable :type="transaction.payable_type" :payable="transaction.payable" />
            </td>
            <td class="text-end"><Icon icon="angle-right" /></td>
        </tr>
    </Datatable>
</template>

<script>
    import { Boolean, Datatable, Dropdown } from '@tech_hexeko/design-system'
    import SelectedPaymentMethod from '@/components/layout/SelectedPaymentMethod'
    import Helpers from '@tech_hexeko/hexeko-api-classes/Helpers'
    import TypesFilter from '@/components/Types/TypesFilter.vue'
    import Payable from '@/components/PaymentIntents/Payable'

    export default {
        name: 'PaymentTransactions',
        components: {
            Datatable,
            Dropdown,
            SelectedPaymentMethod,
            TypesFilter,
            Payable,
        },
        props: {
            hideActions: Boolean,
            params: {
                type: Object,
                default: () => ({}),
            },
        },
        data() {
            return {
                loading: true,
                pagination: { current_page: 1 },
                search: null,
                transactions: [],
                selectedPayableType: '',
                payableTypes: [
                    { type: 'allowance', name: 'Allowance' },
                    { type: 'booking_request', name: 'Booking Request' },
                    { type: 'card_transaction', name: 'Card Transaction' },
                    { type: 'expense_request', name: 'Expense Request' },
                ],
                timeout: '',
                selectedTypes: [],
            }
        },
        computed: {
            filters() {
                return {
                    payable_type: this.selectedPayableType,
                    types: this.selectedTypes,
                }
            },
        },
        methods: {
            async fetchData(search, filters) {
                this.loading = true
                try {
                    const response = await this.$api.paymentTransactions.get(
                        this.mergeParams({
                            search,
                            page: this.pagination.current_page,
                            includes: ['payer', 'payment_intent.payable', 'payment_method.source'],
                            isCancellable: true,
                            ...filters,
                        })
                    )
                    this.transactions = response.data
                    this.pagination = response.meta.pagination
                    this.loading = false
                } catch (error) {
                    if (error.code !== 'ERR_CANCELED') {
                        this.loading = false
                        throw error
                    }
                }
            },
            mergeParams(params) {
                if (this.params.user) this.params.payer = this.params.user
                return Helpers.cleanObject(Object.assign(this.params, params))
            },
            openTransactionPanel(transaction) {
                let panel = this.openPanel('PaymentTransactionPanel', transaction.id)
                panel.promise.then((result) => {
                    if (result && result.refresh) {
                        return this.fetchData(this.search, this.filters)
                    }
                })
            },
            togglePayableType(type) {
                this.selectedPayableType = this.selectedPayableType === type ? '' : type
            },
            setSearch(search) {
                this.search = search
                this.fetchData(search, this.filters)
            },
            formatPayableType(type) {
                const payableType = this.payableTypes.find((payableType) => payableType.type === type)
                return payableType ? payableType.name : type
            },
        },
        mounted() {
            this.fetchData()
        },
        watch: {
            filters: {
                handler(filters) {
                    if (this.timeout) {
                        clearTimeout(this.timeout)
                    }
                    this.timeout = setTimeout(() => {
                        this.pagination.current_page = 1
                        this.fetchData(this.search, filters)
                    }, 400)
                },
            },
        },
    }
</script>
